import { useEffect, useMemo, useState, Fragment } from "react";
import api, { apiError, assetUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Receipt, DownloadSimple, CircleNotch, Plus, Trash, FloppyDisk, Package, CaretDown, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const badge = { generado: "bg-white/10 text-neutral-300", pedido: "bg-gold/20 text-gold", en_produccion: "bg-blue-500/20 text-blue-300", completado: "bg-emerald-500/20 text-emerald-300" };
const slug = (s) => String(s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

export default function Orders() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("sales");
  return (
    <div className="p-8 max-w-6xl">
      <h1 className="text-3xl font-bold tracking-tight">{t("dashorders.title")}</h1>
      <p className="text-neutral-400 mt-1 mb-6">{t("dashorders.subtitle")}</p>
      <div className="flex gap-1 mb-6 border-b border-white/10">
        <button onClick={() => setTab("sales")} data-testid="orders-tab-sales" className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${tab === "sales" ? "border-gold text-white" : "border-transparent text-neutral-400 hover:text-white"}`}>Órdenes de venta</button>
        <button onClick={() => setTab("designs")} data-testid="orders-tab-designs" className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${tab === "designs" ? "border-gold text-white" : "border-transparent text-neutral-400 hover:text-white"}`}>Diseños</button>
      </div>
      {tab === "sales" ? <SalesReport /> : <DesignsTab t={t} />}
    </div>
  );
}

export function SalesReport() {
  const [orders, setOrders] = useState([]);
  const [phases, setPhases] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fPhase, setFPhase] = useState("todas");
  const [fSeller, setFSeller] = useState("todos");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(null);
  const onNoteAdded = (oid, log) => setOrders((os) => os.map((x) => x.id === oid ? { ...x, notes_log: log } : x));

  const load = () => Promise.all([
    api.get("/store/orders"), api.get("/store/phases"), api.get("/team"),
  ]).then(([o, p, m]) => { setOrders(o.data); setPhases(p.data); setMembers(m.data); setLoading(false); })
    .catch((e) => { toast.error(apiError(e)); setLoading(false); });
  useEffect(() => { load(); }, []);

  const labelOf = (k) => (phases.find((p) => p.key === k) || {}).label || k;
  const sellerName = (o) => {
    if (o.assigned_to) { const mm = members.find((m) => m.id === o.assigned_to); if (mm) return mm.name; }
    return o.rep_name || "—";
  };

  const setPhase = async (o, key) => {
    const prev = o.fulfillment_status;
    setOrders((os) => os.map((x) => x.id === o.id ? { ...x, fulfillment_status: key } : x));
    try { await api.put(`/store/orders/${o.id}/fulfillment`, { fulfillment_status: key }); toast.success(`Fase actualizada: ${labelOf(key)}`); }
    catch (e) { toast.error(apiError(e)); setOrders((os) => os.map((x) => x.id === o.id ? { ...x, fulfillment_status: prev } : x)); }
  };

  const filtered = useMemo(() => orders.filter((o) => {
    if (fPhase !== "todas" && (o.fulfillment_status || "nuevo") !== fPhase) return false;
    if (fSeller !== "todos") {
      const sid = o.assigned_to || "";
      const sn = o.rep_name || "";
      const mm = members.find((m) => m.id === fSeller);
      if (sid !== fSeller && !(mm && sn && sn === mm.name)) return false;
    }
    if (fFrom && o.created_at && new Date(o.created_at) < new Date(fFrom)) return false;
    if (fTo && o.created_at && new Date(o.created_at) > new Date(fTo + "T23:59:59")) return false;
    if (q) {
      const hay = `${o.order_number || ""} ${o.buyer_name || ""} ${o.buyer_email || ""}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [orders, fPhase, fSeller, fFrom, fTo, q, members]);

  const counts = useMemo(() => {
    const c = {};
    orders.forEach((o) => { const k = o.fulfillment_status || "nuevo"; c[k] = (c[k] || 0) + 1; });
    return c;
  }, [orders]);

  if (loading) return <CircleNotch className="animate-spin text-gold" size={28} />;

  const exportPdf = () => {
    const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
    const rangeTxt = (fFrom || fTo) ? `${fFrom || "inicio"} — ${fTo || "hoy"}` : "Todas las fechas";
    const phaseTxt = fPhase === "todas" ? "Todas las fases" : labelOf(fPhase);
    const summary = `<div style="margin:0 0 14px;color:#444;font-size:12px">Total: ${orders.length} · ` + phases.map((p) => `${p.label}: ${counts[p.key] || 0}`).join(" · ") + `</div>`;
    const head = `<tr style="background:#111;color:#fff"><th style="text-align:left;padding:8px">Pedido</th><th style="text-align:left;padding:8px">Fecha</th><th style="text-align:left;padding:8px">Cliente</th><th style="text-align:left;padding:8px">Vendedor</th><th style="text-align:right;padding:8px">Total</th><th style="text-align:right;padding:8px">Saldo</th><th style="text-align:left;padding:8px">Fase</th></tr>`;
    const body = filtered.map((o) => `<tr style="border-bottom:1px solid #eee"><td style="padding:8px">${o.order_number ? ("#" + o.order_number) : o.id.slice(-6)}</td><td style="padding:8px">${o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}</td><td style="padding:8px">${o.buyer_name || "—"}${o.buyer_email ? `<br><span style="color:#999;font-size:11px">${o.buyer_email}</span>` : ""}</td><td style="padding:8px">${sellerName(o)}</td><td style="text-align:right;padding:8px">${money(o.amount)}</td><td style="text-align:right;padding:8px">${money(o.balance)}</td><td style="padding:8px">${labelOf(o.fulfillment_status || "nuevo")}</td></tr>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Reporte de ordenes y fases</title></head><body style="font-family:Arial,Helvetica,sans-serif;color:#111;padding:24px"><h1 style="margin:0 0 4px">Reporte de órdenes y fases</h1><p style="color:#666;margin:0 0 6px">Fase: ${phaseTxt} · Periodo: ${rangeTxt} · ${filtered.length} órdenes · Generado ${new Date().toLocaleString()}</p>${summary}<table style="width:100%;border-collapse:collapse;font-size:13px"><thead>${head}</thead><tbody>${body}</tbody></table><script>window.onload=function(){window.print();}</script></body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Permite las ventanas emergentes para descargar el PDF."); return; }
    w.document.write(html); w.document.close();
  };

  return (
    <div className="space-y-6">
      <PhaseEditor phases={phases} onSaved={load} />

      <div className="flex flex-wrap gap-2" data-testid="phase-summary">
        <button onClick={() => setFPhase("todas")} className={`px-3 py-2 rounded-xl border text-left ${fPhase === "todas" ? "border-gold bg-[#151208]" : "border-white/10 bg-[#121212]"}`}>
          <div className="text-[11px] text-neutral-400">Todas</div><div className="text-xl font-bold">{orders.length}</div>
        </button>
        {phases.map((p) => (
          <button key={p.key} onClick={() => setFPhase(p.key)} data-testid={`phase-card-${p.key}`} className={`px-3 py-2 rounded-xl border text-left ${fPhase === p.key ? "border-gold bg-[#151208]" : "border-white/10 bg-[#121212]"}`}>
            <div className="text-[11px] text-neutral-400">{p.label}</div><div className="text-xl font-bold">{counts[p.key] || 0}</div>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 items-end">
        <div><div className="text-[11px] text-neutral-500 mb-1">Vendedor</div>
          <select value={fSeller} onChange={(e) => setFSeller(e.target.value)} data-testid="filter-seller" className="bg-white/5 border border-white/15 rounded-lg h-9 px-2 text-sm text-neutral-200">
            <option value="todos" className="bg-[#1a1a1a]">Todos</option>
            {members.map((m) => <option key={m.id} value={m.id} className="bg-[#1a1a1a]">{m.name}</option>)}
          </select></div>
        <div><div className="text-[11px] text-neutral-500 mb-1">Desde</div><Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="filter-from" className="bg-white/5 border-white/15 h-9 w-40" /></div>
        <div><div className="text-[11px] text-neutral-500 mb-1">Hasta</div><Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} data-testid="filter-to" className="bg-white/5 border-white/15 h-9 w-40" /></div>
        <div className="flex-1 min-w-[160px]"><div className="text-[11px] text-neutral-500 mb-1">Buscar</div><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pedido, cliente o correo…" data-testid="filter-search" className="bg-white/5 border-white/15 h-9" /></div>
        {(fPhase !== "todas" || fSeller !== "todos" || fFrom || fTo || q) && <Button variant="outline" size="sm" className="rounded-full border-white/15 h-9" onClick={() => { setFPhase("todas"); setFSeller("todos"); setFFrom(""); setFTo(""); setQ(""); }} data-testid="filter-clear">Limpiar</Button>}
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-neutral-500">{filtered.length} de {orders.length} órdenes</div>
        <Button size="sm" onClick={exportPdf} className="h-9 rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold text-[11px]" data-testid="sales-export-pdf"><DownloadSimple size={14} className="mr-1" weight="bold" /> Descargar PDF</Button>
      </div>

      {filtered.length === 0 ? (
        <div className="border border-dashed border-white/15 rounded-2xl p-16 text-center text-neutral-400"><Receipt size={40} className="mx-auto mb-3 text-neutral-600" weight="duotone" /> No hay órdenes con estos filtros.</div>
      ) : (
        <div className="overflow-x-auto border border-white/10 rounded-xl" data-testid="sales-orders-table">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 border-b border-white/10">
              <th className="p-3">Pedido</th><th className="p-3">Fecha</th><th className="p-3">Cliente</th><th className="p-3">Vendedor</th><th className="p-3 text-right">Total</th><th className="p-3 text-right">Saldo</th><th className="p-3">Fase</th>
            </tr></thead>
            <tbody>
              {filtered.map((o, idx) => (
                <Fragment key={`${o.id}-${idx}`}>
                <tr className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer" data-testid={`sales-order-row-${o.id}`} onClick={() => setExpanded(expanded === o.id ? null : o.id)}>
                  <td className="p-3 font-mono text-xs">
                    <span className="inline-flex items-center gap-1">
                      {expanded === o.id ? <CaretDown size={12} /> : <CaretRight size={12} />}
                      {o.order_number ? `#${o.order_number}` : o.id.slice(-6)}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-neutral-400">{o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}</td>
                  <td className="p-3"><div>{o.buyer_name || "—"}</div><div className="text-[11px] text-neutral-500">{o.buyer_email || ""}</div></td>
                  <td className="p-3 text-xs">{sellerName(o)}</td>
                  <td className="p-3 text-right">${Number(o.amount || 0).toFixed(2)}</td>
                  <td className="p-3 text-right"><span className={Number(o.balance || 0) > 0 ? "text-amber-300" : "text-emerald-300"}>${Number(o.balance || 0).toFixed(2)}</span></td>
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    <select value={o.fulfillment_status || "nuevo"} onChange={(e) => setPhase(o, e.target.value)} data-testid={`order-phase-${o.id}`} className="bg-white/5 border border-white/15 rounded-lg h-8 px-2 text-xs text-neutral-200">
                      {phases.map((p) => <option key={p.key} value={p.key} className="bg-[#1a1a1a]">{p.label}</option>)}
                      {!phases.some((p) => p.key === (o.fulfillment_status || "nuevo")) && <option value={o.fulfillment_status} className="bg-[#1a1a1a]">{o.fulfillment_status}</option>}
                    </select>
                  </td>
                </tr>
                {expanded === o.id && (
                  <tr className="border-b border-white/5 bg-white/[0.015]" data-testid={`sales-order-detail-${o.id}`}>
                    <td colSpan={7} className="p-4"><AdminOrderDetail o={o} onNoteAdded={onNoteAdded} /></td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PhaseEditor({ phases, onSaved }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(phases);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setRows(phases); }, [phases]);

  const add = () => setRows((r) => [...r, { key: "", label: "" }]);
  const upd = (i, label) => setRows((r) => r.map((x, j) => j === i ? { ...x, label } : x));
  const del = (i) => setRows((r) => r.filter((_, j) => j !== i));
  const move = (i, dir) => setRows((r) => { const a = [...r]; const j = i + dir; if (j < 0 || j >= a.length) return a; [a[i], a[j]] = [a[j], a[i]]; return a; });

  const save = async () => {
    const clean = rows.map((x) => ({ key: x.key || slug(x.label) || `fase_${Math.random().toString(36).slice(2, 7)}`, label: (x.label || "").trim() })).filter((x) => x.label);
    if (!clean.length) { toast.error("Debe haber al menos una fase."); return; }
    setSaving(true);
    try { await api.put("/store/settings", { production_phases: clean }); toast.success("Fases guardadas"); onSaved && onSaved(); setOpen(false); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="border border-white/10 rounded-xl bg-[#121212]">
      <button onClick={() => setOpen((v) => !v)} data-testid="phase-editor-toggle" className="w-full flex items-center justify-between p-4 text-left">
        <div className="flex items-center gap-2"><Package size={18} className="text-gold" weight="duotone" /><span className="font-semibold text-sm">Fases de producción</span><span className="text-xs text-neutral-500">({phases.length})</span></div>
        <span className="text-xs text-gold">{open ? "Cerrar" : "Editar"}</span>
      </button>
      {open && (
        <div className="p-4 pt-0 space-y-2" data-testid="phase-editor">
          <p className="text-[11px] text-neutral-500 mb-2">Crea, edita, reordena o elimina las fases por las que pasan tus sortijas. Las órdenes avanzan por estas fases.</p>
          {rows.map((r, i) => (
            <div key={r.key || i} className="flex items-center gap-2" data-testid={`phase-row-${i}`}>
              <div className="flex flex-col">
                <button onClick={() => move(i, -1)} className="text-neutral-500 hover:text-white text-[10px] leading-none">▲</button>
                <button onClick={() => move(i, 1)} className="text-neutral-500 hover:text-white text-[10px] leading-none">▼</button>
              </div>
              <Input value={r.label} onChange={(e) => upd(i, e.target.value)} placeholder="Nombre de la fase" data-testid={`phase-label-${i}`} className="bg-white/5 border-white/15 h-9 flex-1" />
              <button onClick={() => del(i)} data-testid={`phase-del-${i}`} className="text-rose-400 p-1"><Trash size={16} /></button>
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <Button variant="outline" size="sm" className="rounded-full border-white/15 h-9" onClick={add} data-testid="phase-add"><Plus size={14} className="mr-1" /> Añadir fase</Button>
            <Button size="sm" className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold h-9" onClick={save} disabled={saving} data-testid="phase-save"><FloppyDisk size={14} className="mr-1" /> {saving ? "Guardando…" : "Guardar fases"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function DesignsTab({ t }) {
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const STATUS = [
    { v: "generado", l: t("dashorders.status.generado") },
    { v: "pedido", l: t("dashorders.status.pedido") },
    { v: "en_produccion", l: t("dashorders.status.en_produccion") },
    { v: "completado", l: t("dashorders.status.completado") },
  ];
  const load = () => api.get("/designs").then((r) => { setDesigns(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const update = async (id, patch) => {
    try { const { data } = await api.put(`/designs/${id}`, patch); setDesigns((ds) => ds.map((d) => d.id === id ? { ...d, ...data } : d)); }
    catch (e) { toast.error(apiError(e)); }
  };
  const totalCommission = designs.filter((d) => d.status !== "generado").reduce((s, d) => s + (d.commission || 0), 0);
  const totalSales = designs.filter((d) => d.status !== "generado").reduce((s, d) => s + (d.price || 0), 0);
  return (
    <div>
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <div className="border border-white/10 rounded-xl p-5 bg-[#121212]"><div className="text-sm text-neutral-400">{t("dashorders.designsGenerated")}</div><div className="text-3xl font-bold">{designs.length}</div></div>
        <div className="border border-white/10 rounded-xl p-5 bg-[#121212]"><div className="text-sm text-neutral-400">{t("dashorders.salesRegistered")}</div><div className="text-3xl font-bold">${totalSales.toFixed(2)}</div></div>
        <div className="border border-gold/30 rounded-xl p-5 bg-[#151208]"><div className="text-sm text-gold">{t("dashorders.platformCommission")}</div><div className="text-3xl font-bold text-gold">${totalCommission.toFixed(2)}</div></div>
      </div>
      {loading ? <CircleNotch className="animate-spin text-gold" size={28} /> : designs.length === 0 ? (
        <div className="border border-dashed border-white/15 rounded-2xl p-16 text-center text-neutral-400">
          <Receipt size={40} className="mx-auto mb-3 text-neutral-600" weight="duotone" /> {t("dashorders.empty")}
        </div>
      ) : (
        <div className="space-y-3" data-testid="orders-list">
          {designs.map((d) => (
            <div key={d.id} className="flex flex-wrap items-center gap-4 border border-white/10 rounded-xl p-4 bg-[#121212]" data-testid={`order-row-${d.id}`}>
              <img src={assetUrl(d.url)} alt="" className="w-16 h-16 rounded-lg object-cover bg-neutral-900" />
              <div className="flex-1 min-w-[140px]">
                <div className="font-semibold">{d.product_name}{d.metal ? <span className="text-neutral-500 font-normal"> · {d.metal}</span> : null}</div>
                <div className="text-xs text-neutral-500">{new Date(d.created_at).toLocaleString()}</div>
                {d.customer_name && <div className="text-xs text-neutral-400 mt-0.5">{t("dashorders.customer")}: {d.customer_name}{d.customer_email ? ` (${d.customer_email})` : ""}</div>}
                {d.ring_size && <div className="text-xs text-neutral-400">{t("dashorders.size")}: {d.ring_size}</div>}
                {d.config?.engraving_text && <div className="text-xs text-neutral-400">{t("dashorders.engraving")}: "{d.config.engraving_text}"</div>}
                {d.notes && <div className="text-xs text-gold/80 mt-0.5">{t("dashorders.note")}: {d.notes}</div>}
              </div>
              <Input type="number" placeholder={t("dashorders.pricePlaceholder")} defaultValue={d.price || ""} onBlur={(e) => e.target.value && update(d.id, { price: e.target.value })}
                className="w-28 bg-white/5 border-white/15 h-9" data-testid={`order-price-${d.id}`} />
              <div className="text-sm text-gold w-24">{d.commission != null ? t("dashorders.comm", { amount: d.commission }) : "—"}</div>
              <Select value={d.status} onValueChange={(v) => update(d.id, { status: v })}>
                <SelectTrigger className={`w-40 h-9 border-0 ${badge[d.status] || "bg-white/10"}`} data-testid={`order-status-${d.id}`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/15 text-neutral-200">
                  {STATUS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                </SelectContent>
              </Select>
              <a href={assetUrl(d.url)} download target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" className="rounded-full border-white/15" data-testid={`order-download-${d.id}`}><DownloadSimple /></Button>
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


const PM_LABEL = { efectivo: "Efectivo", tarjeta: "Tarjeta", transferencia: "Transferencia", ath_movil: "ATH Móvil", paypal: "PayPal", otro: "Otro", stripe: "Stripe (en línea)" };

function AdminOrderDetail({ o, onNoteAdded }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const subtotal = Number(o.subtotal || o.amount || 0);
  const tax = Number(o.tax || 0), shipping = Number(o.shipping || 0);
  const discOff = o.discount ? Number(o.discount.amount_off || 0) : 0;
  const payments = o.payments || [];
  const log = o.notes_log || [];
  const er = o.email_results || {};
  const addr = o.buyer_address || {};
  const fmtAddr = [addr.line1, addr.line2, [addr.city, addr.state].filter(Boolean).join(", "), addr.zip, addr.country].filter(Boolean).join(" · ");
  const addNote = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { const { data } = await api.post(`/store/orders/${o.id}/note`, { text: text.trim() }); setText(""); toast.success("Nota añadida"); onNoteAdded(o.id, data.notes_log); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-5 text-sm">
      <div className="space-y-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">Cliente</div>
          <div className="text-neutral-200">{o.buyer_name || "—"}</div>
          <div className="text-xs text-neutral-400">{o.buyer_email || ""}{o.buyer_phone ? ` · ${o.buyer_phone}` : ""}</div>
          {fmtAddr && <div className="text-xs text-neutral-500 mt-0.5">📍 {fmtAddr}</div>}
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">Artículos</div>
          <div className="space-y-2">
            {(o.items || []).map((i, ix) => (
              <div key={ix} className="border border-white/10 rounded-lg p-2 bg-[#121212]" data-testid={`admin-order-item-${o.id}-${ix}`}>
                <div className="text-neutral-200 font-medium text-xs">{i.quantity}× {i.name} <span className="text-neutral-500 font-normal">· ${Number(i.unit_amount || 0).toFixed(2)}</span></div>
                {(i.material || i.size) && <div className="text-[11px] text-neutral-400">{i.material ? `Material: ${i.material}` : ""}{i.material && i.size ? " · " : ""}{i.size ? `Talla: ${i.size}` : ""}</div>}
                {i.customization_text && <div className="text-[11px] text-neutral-400">Personalización: {i.customization_text}</div>}
                {(i.design_image_url || (i.custom_logo_urls && i.custom_logo_urls.length > 0)) && (
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {i.design_image_url && <img src={assetUrl(i.design_image_url)} alt="Diseño" className="h-16 w-16 object-cover rounded border border-white/15" data-testid={`admin-order-design-${o.id}-${ix}`} />}
                    {(i.custom_logo_urls || []).map((u, k) => <img key={k} src={assetUrl(u)} alt="Logo" className="h-14 w-14 object-contain rounded border border-white/15 bg-white/5 p-0.5" data-testid={`admin-order-logo-${o.id}-${ix}-${k}`} />)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="border-t border-white/10 pt-2 text-xs text-neutral-400 space-y-0.5">
          <div className="flex justify-between"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
          {discOff > 0 && <div className="flex justify-between text-emerald-400"><span>Descuento</span><span>-${discOff.toFixed(2)}</span></div>}
          {tax > 0 && <div className="flex justify-between"><span>Impuesto</span><span>${tax.toFixed(2)}</span></div>}
          {shipping > 0 && <div className="flex justify-between"><span>Envío</span><span>${shipping.toFixed(2)}</span></div>}
          <div className="flex justify-between text-neutral-100 font-semibold"><span>Total</span><span>${Number(o.amount || 0).toFixed(2)}</span></div>
          <div className="flex justify-between"><span>Pagado</span><span className="text-emerald-300">${Number(o.amount_paid || 0).toFixed(2)}</span></div>
          <div className="flex justify-between"><span>Saldo</span><span className={Number(o.balance || 0) > 0 ? "text-amber-300" : "text-emerald-300"}>${Number(o.balance || 0).toFixed(2)}</span></div>
        </div>
      </div>
      <div className="space-y-3">
        {payments.length > 0 && (
          <div>
            <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">Historial de pagos</div>
            {payments.map((p, k) => (
              <div key={k} className="flex justify-between text-xs text-neutral-400"><span>{(p.at || "").slice(0, 10)} · {PM_LABEL[p.method] || p.method || "—"}{p.note ? ` · ${p.note}` : ""}</span><span className="text-emerald-300">${Number(p.amount || 0).toFixed(2)}</span></div>
            ))}
          </div>
        )}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">Correos</div>
          <div className="flex flex-wrap gap-3">
            {["cliente", "vendedor", "administración"].map((role) => {
              const r = er[role];
              if (!r) return <span key={role} className="text-[11px] text-neutral-600">{role}: —</span>;
              return <span key={role} className={`text-[11px] ${r.sent ? "text-emerald-300" : "text-rose-400"}`} title={r.error || ""}>{role}: {r.sent ? "✓" : "✗"}</span>;
            })}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">Notas (historial)</div>
          {log.length > 0 ? (
            <div className="space-y-1.5 mb-2 max-h-48 overflow-y-auto">
              {log.map((n, i) => (
                <div key={i} className="text-xs bg-white/5 rounded p-2" data-testid={`admin-order-note-${o.id}-${i}`}>
                  <div className="text-neutral-200 whitespace-pre-wrap">{n.text}</div>
                  <div className="text-[10px] text-neutral-500 mt-0.5">{n.by || "—"}{n.role ? ` · ${n.role}` : ""}{n.at ? ` · ${String(n.at).slice(0, 16).replace("T", " ")}` : ""}</div>
                </div>
              ))}
            </div>
          ) : <p className="text-[11px] text-neutral-500 mb-2">Sin notas todavía.</p>}
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Escribe una nota…" className="w-full bg-white/5 border border-white/15 rounded p-2 text-xs text-neutral-200" data-testid={`admin-order-note-input-${o.id}`} />
          <Button size="sm" className="mt-1 h-7 rounded-full bg-gold text-obsidian hover:bg-gold/90 text-[11px] px-3" onClick={addNote} disabled={busy || !text.trim()} data-testid={`admin-order-note-save-${o.id}`}>Añadir nota</Button>
        </div>
      </div>
    </div>
  );
}
